// Автентифікація: AuthProvider (бутстрап через POST /api/auth/refresh), useAuth(), can(), ProtectedRoute, GuestOnly.
// can() на клієнті — лише для приховування UI (SPEC розд. 5); права перевіряє сервер.
import type { Role } from '@unisched/shared';
import { createContext, use, useCallback, useEffect, useMemo, useState, type Context, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { api, hasSessionHint, markUserActivity, refreshSession, setAccessToken, setApiHandlers, setSessionHint } from './api';
import { rolesCan, type Permission } from './permissions';
import { queryClient } from './query';
import type { AuthSession, AuthUser, RoleAssignmentDto } from './types';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Унікальні ролі користувача. */
  roles: Role[];
  /** Бутстрап не зміг перевірити сесію (сервер недоступний / ліміт запитів) — текст для банера. */
  bootError: string | null;
  /** Користувач щойно вийшов сам (ProtectedRoute веде на /login без ?next). */
  loggedOut: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** Перечитати GET /api/auth/me. */
  reloadUser: () => Promise<AuthUser | null>;
  hasRole: (...roles: Role[]) => boolean;
  /** Чи показувати дію (клієнтська матриця SPEC розд. 5, без області). */
  can: (permission: Permission) => boolean;
  /** Чи є серверна дія AccessService (з /api/auth/me → permissions), напр. 'directories.write'. Лише для показу UI. */
  hasAction: (action: string) => boolean;
}

// Контекст переживає HMR модуля (інакше споживачі бачать новий порожній контекст у dev).
const AuthContext: Context<AuthContextValue | null> =
  (import.meta.hot?.data.authContext as Context<AuthContextValue | null> | undefined) ??
  createContext<AuthContextValue | null>(null);
if (import.meta.hot) import.meta.hot.data.authContext = AuthContext;

// Бутстрап — один раз на завантаження сторінки (StrictMode монтує ефекти двічі).
let bootstrapPromise: ReturnType<typeof refreshSession> | null = null;

/** Нормалізація користувача з API: roles може прийти як roleAssignments або як масив рядків. */
export function normalizeUser(raw: unknown): AuthUser {
  const u = (raw ?? {}) as Record<string, unknown>;
  const src = (Array.isArray(u.roles) ? u.roles : Array.isArray(u.roleAssignments) ? u.roleAssignments : []) as unknown[];
  const roles: RoleAssignmentDto[] = src.map((r) => (typeof r === 'string' ? { role: r as Role } : (r as RoleAssignmentDto)));
  return {
    id: String(u.id ?? ''),
    email: String(u.email ?? ''),
    fullName: String(u.fullName ?? u.name ?? u.email ?? ''),
    roles,
    teacherId: (u.teacherId as string | null | undefined) ?? null,
    teacherName: (u.teacherName as string | null | undefined) ?? null,
    studentGroupId: (u.studentGroupId as string | null | undefined) ?? null,
    studentGroupName: (u.studentGroupName as string | null | undefined) ?? null,
    lastLoginAt: (u.lastLoginAt as string | null | undefined) ?? null,
    permissions: Array.isArray(u.permissions) ? u.permissions.map(String) : [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [loggedOut, setLoggedOut] = useState(false);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setSessionHint(false);
    setUser(null);
    setStatus('anonymous');
    queryClient.clear();
  }, []);

  useEffect(() => {
    setApiHandlers({
      onSessionExpired: clearSession,
      onRefreshed: (session) => {
        if (session.user) setUser(normalizeUser(session.user));
      },
    });
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;
    // Без позначки сесії (гість) — не питаємо сервер: refresh без cookie завжди 401.
    bootstrapPromise ??= hasSessionHint() ? refreshSession() : Promise.resolve({ kind: 'invalid' } as const);
    void bootstrapPromise.then((r) => {
      if (cancelled) return;
      if (r.kind === 'ok') {
        setUser(normalizeUser(r.session.user));
        setStatus('authenticated');
      } else {
        setBootError(
          r.kind === 'network'
            ? 'Сервер недоступний. Не вдалося перевірити сесію — спробуйте пізніше.'
            : r.kind === 'limited'
              ? r.message
              : null,
        );
        setStatus('anonymous');
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const applySession = useCallback((session: AuthSession): AuthUser => {
    setAccessToken(session.accessToken);
    setSessionHint(true);
    const u = normalizeUser(session.user);
    queryClient.clear();
    setUser(u);
    setStatus('authenticated');
    setBootError(null);
    setLoggedOut(false);
    return u;
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await api.post<AuthSession>('/auth/login', { email: email.trim(), password }, { noRefresh: true });
      // вхід — дія користувача: лічильник бездіяльності (веб і сервер, ТЗ 4.1.5.3) починається з нуля
      markUserActivity();
      return applySession(session);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { noRefresh: true });
    } catch {
      // Навіть якщо сервер недоступний — локально виходимо.
    }
    setLoggedOut(true);
    clearSession();
  }, [clearSession]);

  const reloadUser = useCallback(async () => {
    try {
      const me = normalizeUser(await api.get<AuthUser>('/auth/me'));
      setUser(me);
      return me;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const roles = [...new Set((user?.roles ?? []).map((r) => r.role))];
    return {
      status,
      user,
      roles,
      bootError,
      loggedOut,
      login,
      logout,
      reloadUser,
      hasRole: (...wanted) => roles.some((r) => wanted.includes(r)),
      can: (permission) => rolesCan(roles, permission),
      hasAction: (action) => !!user?.permissions.includes(action),
    };
  }, [status, user, bootError, loggedOut, login, logout, reloadUser]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth() поза <AuthProvider>');
  return ctx;
}

/** Поточний користувач у захищених маршрутах (гарантовано не null). */
export function useCurrentUser(): AuthUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser() без автентифікації');
  return user;
}

/** Скорочення: const can = useCan(); can('directories.edit'). */
export function useCan(): (permission: Permission) => boolean {
  return useAuth().can;
}

export function FullScreenLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg" role="status" aria-live="polite">
      <div className="flex items-center gap-3 text-sm text-muted">
        <span className="size-4 animate-spin rounded-full border-2 border-border-strong border-t-primary" />
        Завантаження…
      </div>
    </div>
  );
}

/** Захищений маршрут: поки бутстрап — лоадер; без сесії — /login?next=<поточний шлях>. */
export function ProtectedRoute({ children }: { children?: ReactNode }) {
  const { status, loggedOut } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'anonymous') {
    if (loggedOut) return <Navigate to="/login" replace />;
    const next = location.pathname + location.search;
    const to = next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login';
    return <Navigate to={to} replace />;
  }
  return children ? <>{children}</> : <Outlet />;
}

/** Лише для гостей (сторінка входу): автентифікованого відправляє на `next` або на головну. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={safeNext(next)} replace />;
  }
  return <>{children}</>;
}

/** Лише внутрішні шляхи (захист від open redirect). */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/login')) return '/';
  return next;
}
